import { useState, useCallback } from 'react';
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const MULTIPART = { headers: { 'Content-Type': 'multipart/form-data' } };

const RESULT_STYLE = {
  create: { label: 'Will create', className: 'bg-green-100 text-green-800 border-green-300' },
  duplicate: { label: 'Already exists', className: 'bg-amber-100 text-amber-800 border-amber-300' },
  error: { label: 'Error', className: 'bg-red-100 text-red-800 border-red-300' },
};

const formatDate = (iso) => {
  if (!iso) return '-';
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return String(iso);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const errorText = (err) => {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' ? detail : 'Something went wrong. Please try again.';
};

const peopleText = (t) => {
  const parts = [];
  if (t._responsibleNames?.length) parts.push(`R: ${t._responsibleNames.join(', ')}`);
  if (t._accountableNames?.length) parts.push(`A: ${t._accountableNames.join(', ')}`);
  return parts.length ? parts.join('  \u2022  ') : '-';
};

export default function WorkBriefImportModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1); // 1: choose file, 2: preview, 3: done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);

  const resetState = useCallback(() => {
    setStep(1);
    setFile(null);
    setPreview(null);
    setOnlyProblems(false);
    setIsLoading(false);
    setResult(null);
  }, []);

  const handleClose = () => {
    resetState();
    onOpenChange(false);
  };

  const handleFileSelect = async (e) => {
    const selected = e.target.files?.[0];
    e.target.value = ''; // lets the same file be chosen again after it has been fixed
    if (!selected) return;
    if (!/\.(xlsx|xls|csv)$/i.test(selected.name)) {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
      return;
    }
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', selected);
      const response = await axios.post(`${API}/api/work-brief/preview`, formData, MULTIPART);
      setFile(selected);
      setPreview(response.data);
      setOnlyProblems(false);
      setStep(2);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/api/work-brief/import`, formData, MULTIPART);
      setResult(response.data);
      setStep(3);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setIsLoading(false);
    }
  };

  const rows = preview?.tasks || [];
  const shownRows = onlyProblems ? rows.filter((t) => t._action !== 'create') : rows;
  const createCount = preview?.createCount || 0;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5" />
            Import Work Brief
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-2">
          {step === 1 && (
            <div className="space-y-4">
              <label
                className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-slate-300 p-10 text-center cursor-pointer hover:border-slate-400"
              >
                {isLoading ? <Loader2 className="h-8 w-8 animate-spin" /> : <Upload className="h-8 w-8 text-slate-500" />}
                <span className="font-medium">Choose the filled-in New Work Brief sheet</span>
                <span className="text-sm text-slate-500">Excel (.xlsx, .xls) or CSV</span>
                <input
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  className="hidden"
                  onChange={handleFileSelect}
                  disabled={isLoading}
                  data-testid="wb-file-input"
                />
              </label>
              <p className="text-sm text-slate-600">
                Clients, divisions, jobs and people must match the dashboard exactly. Anything that does not match is shown
                in the next step, and nothing is created until you confirm.
              </p>
            </div>
          )}

          {step === 2 && preview && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2" data-testid="wb-summary">
                <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">To create: {createCount}</Badge>
                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">Already exist: {preview.duplicateCount}</Badge>
                <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">Errors: {preview.errorCount}</Badge>
                <span className="text-sm text-slate-500">Blank rows ignored: {preview.blankSkipped}</span>
                <label className="ml-auto flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={onlyProblems}
                    onChange={(e) => setOnlyProblems(e.target.checked)}
                    data-testid="wb-only-problems"
                  />
                  Show only rows that will not be created
                </label>
              </div>

              {preview.errorCount > 0 && (
                <p className="flex items-start gap-2 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  {preview.errorCount} row(s) have errors and will not be created. Fix them in the sheet and upload it again.
                  Rows that were already created are skipped, so nothing is doubled.
                </p>
              )}

              {rows.length === 0 ? (
                <p className="text-sm text-slate-600">No job rows were found in this file.</p>
              ) : (
                <div className="overflow-auto rounded border max-h-[50vh]">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-left sticky top-0">
                      <tr>
                        <th className="p-2">Row</th>
                        <th className="p-2">Client</th>
                        <th className="p-2">Division</th>
                        <th className="p-2">Job</th>
                        <th className="p-2">Due</th>
                        <th className="p-2">People</th>
                        <th className="p-2">Result</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shownRows.map((t) => {
                        const style = RESULT_STYLE[t._action] || RESULT_STYLE.error;
                        const messages = [...(t._errors || []), ...(t._warnings || [])];
                        const rowBg = t._action === 'error' ? 'bg-red-50' : t._action === 'duplicate' ? 'bg-amber-50' : '';
                        return (
                          <tr key={t._rowNum} className={`border-t align-top ${rowBg}`} data-testid={`wb-row-${t._rowNum}`}>
                            <td className="p-2 text-slate-500">{t._rowNum}</td>
                            <td className="p-2">{t._client || '-'}</td>
                            <td className="p-2">{t._division || '-'}</td>
                            <td className="p-2">
                              <div>{t._job || '-'}</div>
                              {messages.length > 0 && (
                                <ul className="mt-1 space-y-0.5">
                                  {(t._errors || []).map((m, i) => (
                                    <li key={`e${i}`} className="text-xs text-red-700">{m}</li>
                                  ))}
                                  {(t._warnings || []).map((m, i) => (
                                    <li key={`w${i}`} className="text-xs text-amber-700">{m}</li>
                                  ))}
                                </ul>
                              )}
                            </td>
                            <td className="p-2 whitespace-nowrap">{formatDate(t.dueDate)}</td>
                            <td className="p-2">{peopleText(t)}</td>
                            <td className="p-2">
                              <Badge variant="outline" className={style.className}>{style.label}</Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {step === 3 && result && (
            <div className="flex flex-col items-center gap-3 py-8 text-center" data-testid="wb-done">
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <p className="text-lg font-semibold">
                {result.created} job{result.created === 1 ? '' : 's'} created
              </p>
              {result.skippedDuplicates > 0 && (
                <p className="text-sm text-slate-600">{result.skippedDuplicates} row(s) already existed and were skipped.</p>
              )}
              {result.errors > 0 && (
                <p className="text-sm text-red-700">
                  {result.errors} row(s) had errors and were not created. Fix them in the sheet and upload it again.
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="border-t pt-4">
          {step === 1 && (
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
          )}
          {step === 2 && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setStep(1);
                  setFile(null);
                  setPreview(null);
                }}
                disabled={isLoading}
              >
                Choose another file
              </Button>
              <Button onClick={handleImport} disabled={isLoading || createCount === 0} data-testid="wb-create-btn">
                {isLoading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Create {createCount} job{createCount === 1 ? '' : 's'}
              </Button>
            </>
          )}
          {step === 3 && <Button onClick={handleClose}>Close</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
